/* 
Helpers related to usage of vue-i18n -- for internationalization / translations

We have multiple ways of handling translations in the frontend, but vue-i18n is the main one.  
To use it, we need to create instance of VueI18n and pass it to the Vue app when we create it.  
This (creating the Vue instance) happens on each page load, so its in the Django template for each page.
So this setupVueI18n needs to be called in the template for each page, and the resulting VueI18n instance needs to be passed to the Vue app when it is created.
Best way to do this is to just put it all in the spl_base/internal_base/portal_base templates, then override the new Vue() call in child templates only when needed

We are using vue-i18n v8.x, which is the last version that supports Vue 2.x.  Vue 3.x uses vue-i18n v9.x, which has a different API.  So we need to be careful about which version we are using and which API we are using.
vue-i18n v8 docs:  https://kazupon.github.io/vue-i18n/

*/

import VueI18n from 'vue-i18n';
//   Vue.use(VueI18n);  // already in main.js
import merge from 'lodash/merge';

// load the common messages for the app
import globalMessages from './shared/globals';
import { splLocaleWindowVarName, vueI18nInstanceVarName, defaultLocaleObject } from './constants';

// NO - don't do this supportedLocales junk... then you can never add new ones unless you add them here... just unnecessary
// const supportedLocales = Object.keys(globalMessages);  // or define somewhere else....

export default function setupVueI18n({ locale, fallbackLocale = defaultLocaleObject.baseName, pageMessages = {} } = {}) {
  // create the i18n instance with the common messages

  let currentLocale = locale || window[splLocaleWindowVarName] || defaultLocaleObject.baseName;  // default to 'en' if not provided and not in window

  // do not check if currentLocale is in supportedLocales
  // unless you also handle region codes... just pass it in to VueI18n and it will handle the region code stuff, then use fallback if needed
  // TODO - figure out what you want to do with pageMessages and globalMessages.... later

  const messages = {};
  Object.entries(globalMessages).forEach(([locale, globalLocaleMessages]) => {
    messages[locale] = { ...globalLocaleMessages };  // shallow copy of global messages should be fine... not changing, and only local on one page
  });
  // now we DEEP MERGE in the pageMessages (if any) -- but only if they are an object, otherwise ignore -- the deep merge is important because we will likely have some deeply nested stuff here
  if (pageMessages && typeof pageMessages === 'object') {
    Object.entries(pageMessages).forEach(([locale, pageLocaleMessages]) => {
      if (!messages[locale]) {
        messages[locale] = {};  // create an empty object for this locale if it doesn't exist yet
      }
      merge(messages[locale], pageLocaleMessages);  // merge in the page messages for this locale
    });
  }

  const i18n = new VueI18n({
    locale: currentLocale,
    fallbackLocale: fallbackLocale, // set fallback locale
    messages: messages,
    silentFallbackWarn: true,  // this silents the annoying warning when we use things from the global messages that are not defined in a component (basically the point of global messages)
  });

  // we will also stash the new i18n instance on the window so that other code can access it if needed (like when we instantiate new Vue instances in templates)
  window[vueI18nInstanceVarName] = i18n;  // stash the i18n instance on the window so that other code can access it if needed

  return i18n;
}